import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { PermissionBit } from "../../../api";
import { HelperTooltip } from "../../../components/ui-kit/base/helper-tooltip";
import { BaseCheckbox } from "../../../components/ui-kit/fields/base-checkbox";
import { ChoosePencilButton } from "../../../components/ui-kit/tables/cells/cell-choose";
import { Patients } from "../../patients/patients";
import { AdminConsole } from "../admin-console";
import { Clinics } from "../clinics/clinics";
import { ClinicsModal } from "../common/clinics-modal";
import { PatientsModal } from "../common/patients-modal";
import { PhysiciansModal } from "../common/physicians-modal";
import { Physicians } from "../physicians/physicians";
import { LocationsDialog } from "./locations-dialog";
import { isVisibilityGroup, PermissionGroup } from "./permission-groups";
import {
  PERMISSION_ROW_CLASS,
  PermissionRow,
  PermissionSection,
} from "./permissions-picker";
import { type PermissionRules } from "./permissions-utils";
import { Fields } from "./policy-fields";

/**
 * Scope bit whose picker is open, with the list model that picker was opened
 * with. The models load asynchronously and hold the picker's selection, so they
 * are created per opening rather than per render.
 */
type OpenPicker =
  | { bit: PermissionBit.VISIBILITY_MANAGED; model: Patients }
  | { bit: PermissionBit.VISIBILITY_CLINICS; model: Clinics }
  | { bit: PermissionBit.VISIBILITY_PHYSICIAN; model: Physicians }
  | { bit: PermissionBit.VISIBILITY_REGIONS };

/**
 * The visibility scope of a policy: the scope bits, each with the record picker
 * that fills it in right on its own row, so choosing a scope and populating it
 * is one action. `monitoring_active` lives here too.
 *
 * The bits are plain checkboxes - whether the scopes are mutually exclusive is
 * the metadata's business (`forbidden_permissions`), not this component's.
 */
export const VisibilityPicker = observer(function VisibilityPicker(props: {
  fields: Fields;
  groups: PermissionGroup[];
  rules: PermissionRules;
  admin: AdminConsole;
  username: string;
}) {
  const { fields, admin, username } = props;
  const { t } = useLingui();
  const [picker, setPicker] = useState<OpenPicker | null>(null);

  /**
   * Keyed by bit rather than switched on it: the metadata endpoint types `bit`
   * as a plain number, so a bit it adds tomorrow finds no entry here and simply
   * renders without a picker. `open` stays a thunk - the list models are built
   * when the picker opens, not on every render. `hint` says what the records
   * picked grant, which the bit's name alone does not.
   */
  const scopes: Record<
    number,
    { label: string; hint: string; open: () => OpenPicker }
  > = {
    [PermissionBit.VISIBILITY_MANAGED]: {
      label: plural(fields.visible_patient_ids.length, {
        one: "# patient",
        other: "# patients",
      }),
      hint: t`The patients this user can see, picked one by one.`,
      open: () => ({
        bit: PermissionBit.VISIBILITY_MANAGED,
        model: admin.createPatients(),
      }),
    },
    [PermissionBit.VISIBILITY_CLINICS]: {
      label: plural(fields.clinic_uuids.length, {
        one: "# clinic",
        other: "# clinics",
      }),
      hint: t`The clinics whose patients this user can see.`,
      open: () => ({
        bit: PermissionBit.VISIBILITY_CLINICS,
        model: admin.createClinics(),
      }),
    },
    [PermissionBit.VISIBILITY_PHYSICIAN]: {
      label: plural(fields.physician_uuids.length, {
        one: "# physician",
        other: "# physicians",
      }),
      // Not the physicians the user is assigned to - that is the user's own
      // record, edited in the user dialog.
      hint: t`The physicians whose patients this user can see.`,
      open: () => ({
        bit: PermissionBit.VISIBILITY_PHYSICIAN,
        model: admin.createPhysicians(),
      }),
    },
    [PermissionBit.VISIBILITY_REGIONS]: {
      label: plural(fields.locations.length, {
        one: "# country",
        other: "# countries",
      }),
      hint: t`The countries and regions whose patients this user can see.`,
      open: () => ({ bit: PermissionBit.VISIBILITY_REGIONS }),
    },
  };

  return (
    <>
      <PermissionSection
        title={<Trans>Visibility scope</Trans>}
        hint={<Trans>Which patients this user can see.</Trans>}
        wide
      >
        {(props.groups.find(isVisibilityGroup)?.items ?? []).map((item) => {
          const scope = scopes[item.bit];
          return (
            <PermissionRow
              key={item.bit}
              item={item}
              fields={fields}
              rules={props.rules}
              // One height whether or not the row carries a picker, so the
              // rows of the two columns line up.
              className="min-h-10"
              trailing={
                scope && (
                  <>
                    <HelperTooltip content={scope.hint} />
                    <ChoosePencilButton
                      label={scope.label}
                      onClick={() => setPicker(scope.open())}
                    />
                  </>
                )
              }
            />
          );
        })}
      </PermissionSection>

      {/* Not a scope - a section of its own, so it does not read as one. */}
      <PermissionSection title={<Trans>Monitoring</Trans>}>
        <div className={PERMISSION_ROW_CLASS}>
          <BaseCheckbox
            checked={fields.monitoring_active}
            onCheckedChange={(checked) => {
              fields.monitoring_active = checked === true;
            }}
            aria-label={t`Monitoring active`}
            label={
              <span className="self-baseline">
                <Trans>Monitoring active</Trans>
              </span>
            }
          />
        </div>
      </PermissionSection>

      {picker && (
        <ScopePicker
          picker={picker}
          fields={fields}
          username={username}
          onClose={() => setPicker(null)}
        />
      )}
    </>
  );
});

/** The records of one scope bit, picked from the list model it was opened with. */
const ScopePicker = observer(function ScopePicker(props: {
  picker: OpenPicker;
  fields: Fields;
  username: string;
  onClose: () => void;
}) {
  const { picker, fields, username, onClose } = props;

  switch (picker.bit) {
    case PermissionBit.VISIBILITY_MANAGED:
      return (
        <PatientsModal
          title={<Trans>Edit visible patients for: {username}</Trans>}
          preselected={fields.visible_patient_ids}
          mode="multi"
          model={picker.model}
          onClose={onClose}
          onConfirm={({ selected }) => {
            fields.visible_patient_ids = selected;
          }}
        />
      );
    case PermissionBit.VISIBILITY_CLINICS:
      return (
        <ClinicsModal
          title={<Trans>Edit clinics for: {username}</Trans>}
          preselected={fields.clinic_uuids}
          mode="multi"
          model={picker.model}
          onClose={onClose}
          onConfirm={({ selected }) => {
            fields.clinic_uuids = selected;
          }}
        />
      );
    case PermissionBit.VISIBILITY_PHYSICIAN:
      return (
        <PhysiciansModal
          title={<Trans>Edit physicians for: {username}</Trans>}
          preselected={fields.physician_uuids}
          mode="multi"
          model={picker.model}
          onClose={onClose}
          onConfirm={({ selected }) => {
            fields.physician_uuids = selected;
          }}
        />
      );
    case PermissionBit.VISIBILITY_REGIONS:
      return (
        <LocationsDialog
          username={username}
          locations={fields.locations}
          onChange={(locations) => {
            fields.locations = locations;
          }}
          onClose={onClose}
        />
      );
  }
});
